"use client";

import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useTransform,
  useSpring,
} from "framer-motion";

// Мокап строки товара
function ProductRow({
  rank,
  name,
  marketplace,
  sales,
  profit,
  roi,
  isPositive,
}: {
  rank: number;
  name: string;
  marketplace: "WB" | "Ozon";
  sales: string;
  profit: string;
  roi: string;
  isPositive: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-400 w-4">{rank}</span>
        <div className="w-8 h-8 rounded-lg bg-gray-100" />
        <div>
          <div className="text-[13px] font-medium text-gray-900">{name}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                marketplace === "WB"
                  ? "bg-purple-50 text-purple-600"
                  : "bg-blue-50 text-blue-600"
              }`}
            >
              {marketplace}
            </span>
            <span className="text-[11px] text-gray-400">{sales} продаж</span>
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className={`text-sm font-semibold ${isPositive ? "text-green-600" : "text-red-500"}`}>
          {isPositive ? "+" : "−"}₽{profit}
        </div>
        <div className={`text-[11px] ${isPositive ? "text-green-500" : "text-red-400"}`}>
          {isPositive ? "+" : "−"}{roi}%
        </div>
      </div>
    </div>
  );
}

// Интерактивный дашборд с 3D tilt
export function DashboardMockup() {
  const containerRef = useRef<HTMLDivElement>(null);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Плавные пружинные значения для tilt
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [5, -5]), {
    stiffness: 150,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-5, 5]), {
    stiffness: 150,
    damping: 20,
  });

  // Позиция свечения следует за мышью
  const glowX = useSpring(useTransform(mouseX, [-0.5, 0.5], [20, 80]), {
    stiffness: 100,
    damping: 30,
  });
  const glowY = useSpring(useTransform(mouseY, [-0.5, 0.5], [20, 80]), {
    stiffness: 100,
    damping: 30,
  });

  function handleMouseMove(e: React.MouseEvent) {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  }

  function handleMouseLeave() {
    mouseX.set(0);
    mouseY.set(0);
  }

  return (
    <div
      ref={containerRef}
      className="relative z-10 w-full max-w-5xl mt-16 pb-20"
      style={{ perspective: "1200px" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Свечение — следует за курсором */}
      <motion.div
        className="absolute w-[70%] h-[300px] pointer-events-none z-0 blur-[80px] rounded-full"
        style={{
          left: useTransform(glowX, (v) => `${v - 35}%`),
          top: useTransform(glowY, (v) => `${v - 40}%`),
          background:
            "radial-gradient(circle, rgba(168,85,247,0.2), rgba(59,130,246,0.1), transparent 70%)",
        }}
      />

      {/* Дашборд с 3D-наклоном */}
      <motion.div
        className="relative z-10 rounded-xl border border-white/[0.1] shadow-2xl shadow-black/50 overflow-hidden"
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
        }}
        initial={{ opacity: 0, y: 60, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 1.2,
          delay: 0.5,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        {/* Анимированная рамка-свечение по краю */}
        <motion.div
          className="absolute inset-0 rounded-xl pointer-events-none z-20"
          style={{
            background: useTransform(
              [glowX, glowY],
              ([x, y]) =>
                `radial-gradient(600px circle at ${x}% ${y}%, rgba(168,85,247,0.15), transparent 60%)`
            ),
          }}
        />

        {/* Title bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1f] border-b border-white/[0.06]">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
            <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
            <div className="w-3 h-3 rounded-full bg-[#28c840]" />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="bg-white/[0.06] rounded-md px-4 py-1 text-[11px] text-white/50">
              app.mpunit.com/dashboard
            </div>
          </div>
        </div>

        {/* Дашборд — светлая тема */}
        <div className="bg-[#f9fafb] p-4 md:p-6">
          {/* 4 метрики */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="text-[11px] text-gray-500 font-medium">Выручка</div>
              <div className="text-xl font-bold text-gray-900 mt-1">₽2,847,320</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="text-[11px] text-gray-500 font-medium">К перечислению</div>
              <div className="text-xl font-bold text-gray-900 mt-1">₽1,923,180</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="text-[11px] text-gray-500 font-medium">Прибыль до налогов</div>
              <div className="text-xl font-bold text-green-600 mt-1">₽412,180</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="text-[11px] text-gray-500 font-medium">Рентабельность</div>
              <div className="text-xl font-bold text-gray-900 mt-1">24.3%</div>
            </div>
          </div>

          {/* Маркетплейсы */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-semibold text-gray-900">Wildberries</span>
                </div>
                <span className="text-[10px] text-gray-400">Синхр. 5 мин назад</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] text-gray-400">Прибыль</div>
                  <div className="text-lg font-bold text-green-600">₽287,420</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-400">Продажи</div>
                  <div className="text-lg font-bold text-gray-900">1,247</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-semibold text-gray-900">Ozon</span>
                </div>
                <span className="text-[10px] text-gray-400">Синхр. 12 мин назад</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] text-gray-400">Прибыль</div>
                  <div className="text-lg font-bold text-green-600">₽124,760</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-400">Продажи</div>
                  <div className="text-lg font-bold text-gray-900">600</div>
                </div>
              </div>
            </div>
          </div>

          {/* Топ товаров */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="text-xs font-semibold text-gray-900 mb-3">Топ прибыльных товаров</div>
            <ProductRow rank={1} name="Кроссовки Nike Air Max" marketplace="WB" sales="847" profit="128,490" roi="34.2" isPositive />
            <ProductRow rank={2} name="Рюкзак городской" marketplace="Ozon" sales="624" profit="87,320" roi="28.1" isPositive />
            <ProductRow rank={3} name="Чехол для iPhone 16" marketplace="WB" sales="3,412" profit="53,150" roi="41.7" isPositive />
          </div>
        </div>

        {/* Fade out */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#050507] to-transparent pointer-events-none z-10" />
      </motion.div>
    </div>
  );
}
